import { useQuery } from '@tanstack/react-query'
import { api } from './client'

// --- Admin statistics API ------------------------------------------------
// All endpoints accept ?tz=<IANA>&date_from=YYYY-MM-DD&date_to=YYYY-MM-DD.
// Day / hour buckets are computed by the backend in the requested tz.
// Types mirror backend/app/schemas/admin_stats.py.

export interface DailyBucket {
  day: string // 'YYYY-MM-DD' in tz
  total_calls: number
  answered: number
  no_answer: number
  tech_failure: number
  talk_seconds: number // SUM(duration) where duration > 10
  total_duration_seconds: number
  by_reason: Record<string, number>
}

export interface DailySummaryResponse {
  timezone: string
  buckets: DailyBucket[]
  totals: DailyBucket
}

export interface HourlyPoint {
  hour: number // 0..23
  series: Record<string, number>
}

export interface HourlyResponse {
  timezone: string
  points: HourlyPoint[] // always 24
  series_keys: string[] // stable legend order
}

export type HourlyGroupBy = 'total' | 'reason_group' | 'reason'

export type DirectionFilter = 'all' | 'incoming' | 'outgoing'

export interface StatsParams {
  tz: string
  dateFrom?: string // 'YYYY-MM-DD'
  dateTo?: string
  phoneId?: string
  userId?: string
  direction?: DirectionFilter
}

export interface PhoneTableRow {
  phone_id: string
  phone_name: string
  sim_number: string | null
  total_calls: number
  incoming: number
  outgoing: number
  over_60: number
  mid_30_60: number
  under_30: number
  answered: number
  answer_rate: number | null
  unique_numbers: number
  repeats: number
  max_per_number: number
  talk_seconds: number
  avg_talk_seconds: number | null
  max_idle_seconds: number
  sim_change_count: number
  last_call_at: string | null // ISO timestamp; null for idle phones
}

export interface PhonesTableResponse {
  timezone: string
  rows: PhoneTableRow[]
}

export interface PhoneIntervalRow {
  phone_id: string
  phone_name: string
  sim_number: string | null
  interval_count: number
  avg_seconds: number
  median_seconds: number
  min_seconds: number
  max_seconds: number
  stddev_seconds: number
}

export interface PhoneIntervalsResponse {
  timezone: string
  rows: PhoneIntervalRow[]
}

// --- phone-dossier endpoint ----------------------------------------------

export interface PhoneDossierIdentity {
  phone_id: string
  name: string
  sim_number: string | null
  carrier_name: string | null
  phone_model: string | null
  os_version: string | null
  app_version: string | null
  is_online: boolean
  is_active: boolean
  battery_pct: number | null
  battery_charging: boolean | null
  signal_dbm: number | null
  signal_bars: number | null
  network_type: string | null
  audio_mode: string | null
  metrics_updated_at: string | null
  owner_label: string | null
  owner_role: string | null
  created_at: string
  sim_change_count_total: number
  current_sim_hash: string | null
  last_sim_change_at: string | null
  last_sim_change_was_reuse: boolean
}

export interface PhoneDossierPeriodSummary {
  total_calls: number
  incoming: number
  outgoing: number
  answered: number
  answer_rate: number | null
  talk_seconds: number
  avg_talk_seconds: number | null
  unique_numbers: number
  repeats: number
  max_per_number: number
  max_idle_seconds: number
  tech_failures_count: number
  unique_operators_count: number
  sim_change_count_in_period: number
  first_ended_at: string | null
  last_ended_at: string | null
}

export interface PhoneDossierCallRow {
  call_id: string
  started_at: string | null
  ended_at: string
  duration_seconds: number
  direction: string
  number: string
  reason: string
  user_label: string | null
  gap_before_seconds: number | null
  // True iff a chronological predecessor exists but THIS call's
  // started_at is NULL — UI renders «простой неизвестен» instead of
  // suppressing the divider entirely.
  gap_before_unknown: boolean
}

export interface PhoneDossierTimeline {
  calls: PhoneDossierCallRow[]
  truncated: boolean
  total_count: number
}

export interface PhoneDossierSimHistoryRow {
  changed_at: string
  previous_sim_hash: string | null
  new_sim_hash: string
  is_reuse: boolean
}

export interface PhoneDossierTopNumber {
  number: string
  call_count: number
  answered_count: number
  total_talk_seconds: number
}

export interface PhoneDossierReasonBreakdown {
  reason: string
  count: number
}

export interface PhoneDossierOperator {
  user_id: string | null
  label: string
  calls_count: number
  talk_seconds: number
}

export interface PhoneDossierHourlyBucket {
  hour: number
  incoming_count: number
  incoming_talk_seconds: number
  outgoing_count: number
  outgoing_talk_seconds: number
}

export interface PhoneDossierResponse {
  timezone: string
  identity: PhoneDossierIdentity
  period_summary: PhoneDossierPeriodSummary
  timeline: PhoneDossierTimeline
  sim_history: PhoneDossierSimHistoryRow[]
  top_numbers: PhoneDossierTopNumber[]
  reason_breakdown: PhoneDossierReasonBreakdown[]
  operators: PhoneDossierOperator[]
  // Full-period hour-of-day distribution (24 buckets, dossier tz). The
  // heatmap reads this instead of aggregating the truncated timeline, so
  // its «peak hour» reflects every call, not just the last-N tail.
  hourly_buckets: PhoneDossierHourlyBucket[]
}

// Exported for unit testing — the omit-when-absent guards are pure
// query-string logic worth pinning directly.
export function buildQuery(
  params: StatsParams & { groupBy?: HourlyGroupBy },
): string {
  const q = new URLSearchParams()
  q.set('tz', params.tz)
  if (params.dateFrom) q.set('date_from', params.dateFrom)
  if (params.dateTo) q.set('date_to', params.dateTo)
  if (params.phoneId) q.set('phone_id', params.phoneId)
  if (params.userId) q.set('user_id', params.userId)
  if (params.groupBy) q.set('group_by', params.groupBy)
  // direction='all' is the backend default — omit so the URL stays
  // identical to the no-direction case (avoids a cache miss when the
  // user toggles the filter back to "all").
  if (params.direction && params.direction !== 'all') {
    q.set('direction', params.direction)
  }
  return `?${q.toString()}`
}

// Historical aggregates — they don't change second-to-second. A 60s
// staleTime avoids refetch storms while the admin tweaks filters; the
// live "current fleet state" comes from usePhones(), not from here.
const STALE_MS = 60_000

// Tab-gated hook options. Stats page mounts hooks at top level even when
// the Tables / Intervals tabs are visible — without `enabled` the charts
// hooks would refetch on every range tweak while the chart tab is hidden,
// wasting backend roundtrips. Each hook composes its own intrinsic gate
// (e.g. usePhoneDayStats needs phoneId) with the caller-passed flag.
interface StatsHookOptions {
  enabled?: boolean
}

export function useDailySummary(params: StatsParams, opts: StatsHookOptions = {}) {
  return useQuery({
    queryKey: ['stats', 'daily-summary', params],
    queryFn: () =>
      api.get<DailySummaryResponse>(
        `/api/v1/admin/stats/daily-summary${buildQuery(params)}`,
      ),
    staleTime: STALE_MS,
    enabled: opts.enabled ?? true,
  })
}

export function useHourlyDistribution(
  params: StatsParams,
  groupBy: HourlyGroupBy = 'reason_group',
  opts: StatsHookOptions = {},
) {
  return useQuery({
    queryKey: ['stats', 'hourly', { ...params, groupBy }],
    queryFn: () =>
      api.get<HourlyResponse>(
        `/api/v1/admin/stats/hourly${buildQuery({ ...params, groupBy })}`,
      ),
    staleTime: STALE_MS,
    enabled: opts.enabled ?? true,
  })
}

// The Tables tab no longer exposes a direction selector — `incoming` /
// `outgoing` split columns make the filter redundant, and at
// `direction != 'all'` the backend LEFT JOIN zeroes the opposite column.
// `Omit<…, 'direction'>` makes the contract explicit at compile time so
// a caller can't accidentally pass `direction` and have it silently
// dropped at runtime. Backend endpoint still accepts the param for
// backward compatibility with bookmarked URLs.
export function usePhonesTable(params: Omit<StatsParams, 'direction'>) {
  return useQuery({
    queryKey: ['stats', 'phones-table', params],
    queryFn: () =>
      api.get<PhonesTableResponse>(
        `/api/v1/admin/stats/phones-table${buildQuery(params)}`,
      ),
    staleTime: STALE_MS,
  })
}

export function usePhonesIntervals(params: StatsParams) {
  return useQuery({
    queryKey: ['stats', 'phones-intervals', params],
    queryFn: () =>
      api.get<PhoneIntervalsResponse>(
        `/api/v1/admin/stats/phones-intervals${buildQuery(params)}`,
      ),
    staleTime: STALE_MS,
  })
}

export function usePhoneDossier(
  params: StatsParams,
  opts: StatsHookOptions = {},
) {
  return useQuery({
    queryKey: ['stats', 'phone-dossier', params],
    // Caller-passed gate AND the intrinsic phoneId gate — dossier has
    // no meaning without a picked phone.
    enabled: (opts.enabled ?? true) && !!params.phoneId,
    queryFn: () =>
      api.get<PhoneDossierResponse>(
        `/api/v1/admin/stats/phone-dossier/${encodeURIComponent(params.phoneId as string)}${buildQuery(
          { ...params, phoneId: undefined },
        )}`,
      ),
    staleTime: STALE_MS,
  })
}

// Timeline scoped to a single day — backs the dossier day picker. The main
// dossier `timeline` is the recent-500 all-days window (truncates the oldest
// day on busy phones); picking a day fetches THAT day's complete call list
// (date_from = date_to = the picked day), so the count is exact and no row is
// silently dropped.
export function usePhoneDossierTimeline(
  params: StatsParams,
  opts: StatsHookOptions = {},
) {
  return useQuery({
    queryKey: ['stats', 'phone-dossier-timeline', params],
    // Caller-passed gate (a day is picked) AND the intrinsic phoneId gate.
    enabled: (opts.enabled ?? true) && !!params.phoneId,
    queryFn: () =>
      api.get<PhoneDossierTimeline>(
        `/api/v1/admin/stats/phone-dossier/${encodeURIComponent(params.phoneId as string)}/timeline${buildQuery(
          { ...params, phoneId: undefined },
        )}`,
      ),
    staleTime: STALE_MS,
  })
}
